import { EditLine } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashWriteTemplate } from "@mirage/analytics/events/enums/dash_write_template";
import type { Select_Upsell } from "@mirage/analytics/events/types/select_upsell";
import { PAP_Select_Upsell } from "@mirage/analytics/events/types/select_upsell";
import type { Shown_Upsell } from "@mirage/analytics/events/types/shown_upsell";
import { PAP_Shown_Upsell } from "@mirage/analytics/events/types/shown_upsell";
import { type PAPPromptEnum } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/types";
import { SuggestionTile } from "@mirage/mosaics/SuggestionTile/SuggestionTile";
import { useGetDeepLinkUrl } from "@mirage/service-auth/useGetDeepLinkUrl";
import { openURL } from "@mirage/service-platform-actions";
import type { PreConfiguredTemplateID } from "@mirage/shared/compose/assist-template";
import { RouteToSlug } from "@mirage/shared/fss-bridge/bridgeRouting";
import i18n from "@mirage/translations";
import { useEffect, useMemo } from "react";

const LOGGING_PROPS: Select_Upsell["properties"] & Shown_Upsell["properties"] =
  {
    upsellFeature: "dash_write",
    upsellFormat: "banner",
    actionSurface: "chat",
  };

interface EnumTemplateMapping {
  text: string;
  templateId: PreConfiguredTemplateID;
  dashWriteTemplate: DashWriteTemplate;
}

// Helper to get template info by enum
const getTemplateInfoByEnum = (
  enumValue: DashWriteTemplate,
): EnumTemplateMapping => {
  switch (enumValue) {
    case "marketing_blog_post":
      return {
        text: i18n.t("promotion_write_marketing_blog_post"),
        templateId: "marketing_blog_post",
        dashWriteTemplate: "marketing_blog_post",
      };
    case "sales_outreach_email":
      return {
        text: i18n.t("promotion_write_sales_outreach_email"),
        templateId: "sales_outreach_email",
        dashWriteTemplate: "sales_outreach_email",
      };
    default:
      return {
        text: i18n.t("promotion_write_status_report"),
        templateId: "status_report",
        dashWriteTemplate: "status_report",
      };
  }
};

export const PromotionWriteWithTemplateChip = ({
  suggestionEnum,
  onSubmit,
  loading = false,
}: {
  suggestionEnum?: DashWriteTemplate;
  onSubmit?: (suggestionEnum: PAPPromptEnum) => void;
  loading?: boolean;
} = {}) => {
  const { reportPapEvent } = useMirageAnalyticsContext();

  const shouldShowChip = !loading;

  useEffect(() => {
    if (!shouldShowChip) {
      return;
    }

    reportPapEvent(
      PAP_Shown_Upsell({
        ...LOGGING_PROPS,
      }),
    );
  }, [shouldShowChip, reportPapEvent]);

  const { text, templateId, dashWriteTemplate } = getTemplateInfoByEnum(
    suggestionEnum || "status_report",
  );

  const memoizedAuthParams = useMemo(() => ({ templateId }), [templateId]);
  const getWriteWithTemplateUrl = useGetDeepLinkUrl(
    RouteToSlug.WRITE_WITH_TEMPLATE,
    memoizedAuthParams,
  );

  if (!shouldShowChip) {
    return null;
  }

  const handleClick = async () => {
    // Call analytics callback if provided (DashWriteTemplate is subset of PAPPromptEnum)
    if (onSubmit && suggestionEnum) {
      onSubmit(suggestionEnum as PAPPromptEnum); // Type-safe: DashWriteTemplate ⊆ AiLandingPageSuggestion ⊆ PAPPromptEnum
    }

    // Existing upsell logging
    reportPapEvent(
      PAP_Select_Upsell({
        ctaName: "write_with_template",
        dashWriteTemplate,
        ...LOGGING_PROPS,
      }),
    );
    openURL(getWriteWithTemplateUrl());
  };

  return (
    <div data-testid="promotion-write-with-template-chip">
      <SuggestionTile
        text={text}
        display={"inline"}
        size="medium"
        onClick={handleClick}
        customIconSrc={EditLine}
        isExternalLink={true}
        isEmbeddedBridge={true}
      />
    </div>
  );
};
